import styled from 'styled-components'
import { motion } from 'framer-motion'

const getButtonSize = ({ $size }) => `${$size || 40}px`

export const Wrapper = styled(motion.div)`
  position: fixed;
  inset: 0;
  /* Above page content (release tabs sit at z-index: 100) but below app
     modals (StyledModal renders at z-index: 999) so confirmation dialogs
     opened from the sheet — e.g. clear queue — appear on top of it. */
  z-index: 900;
  width: 100%;
  height: 100svh;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralW100};
  background-color: ${({ theme }) => theme.colors.neutralW10};
`

export const SheetViewport = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: calc(env(safe-area-inset-top, 0px) + 24px) 16px
    calc(env(safe-area-inset-bottom, 0px) + 24px);
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.neutralW10};
`

export const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  margin-bottom: 24px;
`

export const HeaderSpacer = styled.span`
  width: 40px;
  height: 40px;
`

export const AlbumArtLarge = styled.img`
  display: block;
  flex: 0 0 auto;
  width: 100%;
  height: auto;
  margin: 0 auto 24px;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  overflow: hidden;
  border: 1.228px solid ${({ theme }) => theme.colors.neutralW40};
  border-radius: 6.141px;
`

export const TrackSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 0 0 auto;
  margin-bottom: 24px;
`

export const TrackInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

export const TrackName = styled.h2`
  display: block;
  max-width: 100%;
  margin: 0 0 4.912px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralW100};
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const TrackArtist = styled.p`
  display: block;
  max-width: 100%;
  margin: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const TrackActionButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex: 0 0 auto;
  width: auto;
`

export const PlayerTimeline = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  height: 14.738px;
  margin-bottom: 9.825px;
  padding: 5px 0;

  .rc-slider {
    height: 8px;
    padding: 0;
  }

  .rc-slider-rail,
  .rc-slider-track {
    top: 0;
    height: 8px;
    border-radius: 999px;
  }

  .rc-slider-handle {
    top: 4px;
    margin-top: -6px;
  }
`

export const TimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  margin-bottom: 16px;
`

export const Time = styled.span`
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
`

export const PlaybackControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 0 0 auto;
  margin-bottom: 24px;
`

export const SheetIconButtonFrame = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: ${({ $disabled, $primary, theme }) => {
    if ($primary) return theme.colors.neutralW20
    return $disabled ? theme.colors.neutralW40 : theme.colors.neutralW100
  }};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: auto;
    background: transparent;
    border-radius: 50%;
    letter-spacing: 0;
    line-height: 1;
  }

  button {
    width: ${getButtonSize};
    min-width: ${getButtonSize};
    height: ${getButtonSize};
    padding: ${({ $primary }) => ($primary ? '8px' : '8.2px')};
    color: currentColor;
    background-color: ${({ $primary, theme }) =>
      $primary ? theme.colors.neutralW100 : 'transparent'} !important;
    border: 0;
    border-radius: 50%;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  }

  button:hover {
    background-color: ${({ $disabled, $primary, theme }) => {
      if ($disabled) return $primary ? theme.colors.neutralW100 : 'transparent'
      return $primary ? theme.colors.neutralW80 : theme.colors.neutralW30
    }} !important;
  }

  button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.interact70};
  }

  svg {
    display: block;
    pointer-events: none;
  }
`

export const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  min-width: 40px;
  height: 40px;
  padding: 8px;
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.neutralW40 : theme.colors.neutralW100};
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};

  &:hover {
    background-color: ${({ disabled, theme }) =>
      disabled ? 'transparent' : theme.colors.neutralW30};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.interact70};
  }

  svg {
    display: block;
    pointer-events: none;
  }
`

export const QueueSection = styled.section`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-height: 0;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.neutralW10};
`

export const QueueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  margin-bottom: 24px;
  padding: 8px 0;
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
`

export const QueueClearButton = styled.button`
  padding: 0;
  color: ${({ theme }) => theme.colors.neutralW70};
  background: transparent;
  border: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: underline;
  cursor: pointer;
  text-transform: none;

  &:hover {
    color: ${({ theme }) => theme.colors.neutralW100};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 2px 0 ${({ theme }) => theme.colors.interact70};
  }
`

export const QueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
`

export const QueueRow = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto 32px;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 50px;
  padding: 8px 0;
  color: ${({ theme }) => theme.colors.neutralW100};
  background: ${({ theme }) => theme.colors.neutralW10};
  border: 0;
  border-radius: 5px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutralW30};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.interact70};
  }
`

export const QueueArtwork = styled.img`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.neutralW20};
  border-radius: 5px;
  object-fit: cover;
`

export const QueueItemText = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
`

export const QueueTitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  width: 100%;
`

export const QueueTitle = styled.span`
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralW90};
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const QueueArtist = styled.span`
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const QueueBadges = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
`

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 14px;
  padding: 0 4px;
  color: ${({ theme }) => theme.colors.neutralW20};
  background: ${({ theme }) => theme.colors.neutralW80};
  border-radius: 3px;
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
`

export const QueueItemDuration = styled.span`
  justify-self: end;
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
`

export const QueueItemActions = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: end;
`

export const QueueMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 7px;
  color: ${({ theme }) => theme.colors.neutralW100};
  background: transparent;
  border: 0;
  border-radius: 50px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutralW30};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.interact70};
  }

  svg {
    display: block;
    width: 18px;
    height: 18px;
    pointer-events: none;
  }
`

export const EmptyQueueText = styled.p`
  margin: 16px 0;
  color: ${({ theme }) => theme.colors.neutralW60};
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
`

export const AutoplayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  min-height: 37px;
  padding: 8px 0;
`

export const AutoplayLabel = styled.span`
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`
